import { useEffect, useState } from "react";
import {
  FaBuilding,
  FaCheckSquare,
  FaClipboardList,
  FaFileAlt,
  FaFolderOpen,
  FaIdCard,
  FaMinus,
  FaPhoneAlt,
  FaPlus,
  FaRegClock,
  FaSave,
  FaSearch,
  FaTh,
  FaTimes,
  FaUniversity,
  FaUserTie,
} from "react-icons/fa";
import ActionPageLayout from "../layouts/ActionPageLayout";
import usePageTitle from "../hooks/usePageTitle";
import {
  fetchLeadApplicationDedupe,
  fetchEligibilityCheckList,
  fetchEligibilityCheckOptions,
  fetchEligibilityResultForm,
  saveEligibilityCheckResult,
  searchEligibilityCheck,
} from "../services/eligibilityCheckApi";
import { fetchApplicationByLeadId } from "../services/applicationApi";
import { searchPilInfo } from "../services/pilInfoApi";
import { pullCrifBureauReportCallCenter } from "../services/crifBureauApi";

const inputClass =
  "h-[38px] w-full rounded-md border border-[#d8dee6] bg-white px-3 text-base outline-none transition placeholder:text-[#4b5563] focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.18)]";
const eligibilityListLimit = 10;

const summaryRows = [
  ["report_id", "report_date", "score"],
  ["-", "-", "-"],
];

const dedupeRows = [
  ["Pan Number", "Phone Count", "Phone Numbers"],
  ["-", "-", "-"],
];

const existingLoanRows = [
  [
    "Loan Acc No",
    "Application No",
    "Client No",
    "Product Details",
    "Loan Status",
  ],
  ["-", "-", "-", "-", "-"],
  [
    "Month Disbursal",
    "Disbursal Amount",
    "Loan Irr",
    "Loan Tenure",
    "Emi Amount",
  ],
  ["-", "-", "-", "-", "-"],
];

const existingLoanFieldLabels = {
  Loan_acc_no: "Loan Acc No",
  Application_no: "Application No",
  Client_no: "Client No",
  Product_details: "Product Details",
  Product_sub_variant: "Product Sub Variant",
  Loan_status: "Loan Status",
  month_disbursal: "Month Disbursal",
  Disbursal_amount: "Disbursal Amount",
  Loan_IRR: "Loan IRR",
  Loan_tenure: "Loan Tenure",
  EMI_amount: "EMI Amount",
  Loan_Maturity_date: "Loan Maturity Date",
  Customer_age: "Customer Age",
  customer_contact: "Customer Contact",
  customer_email: "Customer Email",
  customer_PAN_no: "Customer PAN No",
  Pidilite_id: "Pidilite ID",
  Prin_repaid_amt: "Principal Repaid Amount",
  Cur_Prin_Outstanding: "Current Principal Outstanding",
  first_bounce_month: "First Bounce Month",
  last_bounce_month: "Last Bounce Month",
  no_presentation: "No. of Presentations",
  no_nach_clearance: "No. of NACH Clearances",
  no_bounce: "No. of Bounces",
  no_bkt: "No. of Buckets",
  no_90plus: "No. of 90+",
  no_180plus: "No. of 180+",
};

const enquiryRows = [
  ["Report ID", "Gender", "PAN", "Passport", "UID"],
  ["-", "-", "-", "-", "-"],
];

const loanDemographyRows = [
  ["Report ID", "Name1", "DOB1", "PAN1", "CKYC1"],
  ["-", "-", "-", "-", "-"],
];

const addressRows = [
  ["Address 1", "-", "-", "-"],
  ["Address 2", "-", "-", "-"],
  ["Address 3", "-", "-", "-"],
];

const phoneRows = [
  ["Phone 1", "-", "-", "-"],
  ["Phone 2", "-", "-", "-"],
  ["Phone 3", "-", "-", "-"],
];

const emailRows = [
  ["Email 1", "-", "-", "-"],
  ["Email 2", "-", "-", "-"],
  ["Email 3", "-", "-", "-"],
];

const emptyRecordDetails = {
  personal: {
    contractorName: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  },
  club: {
    clubId: "",
    clubName: "",
    clubLocation: "",
    bdName: "",
    bdMobile: "",
  },
  pil: {
    membershipNumber: "",
    memberSince: "",
    memberSinceDisplay: "",
    memberSinceInput: "",
    classification: "",
  },
  banking: {
    bankingPointsQTD: "",
    bankingQTDKgs: "",
    bankingMTDKgs: "",
    bankingYTDKgs: "",
    loyaltyPoints: "",
  },
};

function getRecordDetails(record) {
  return {
    ...emptyRecordDetails,
    personal: {
      ...emptyRecordDetails.personal,
      contractorName: record?.customerName || "",
      address: record?.address || "",
      city: record?.city || "",
      state: record?.state || "",
      pincode: record?.pincode || "",
    },
    pil: {
      ...emptyRecordDetails.pil,
      membershipNumber: record?.membershipNumber || "",
      memberSince: record?.memberSince || "",
      memberSinceInput: record?.memberSinceInput || record?.memberSince || "",
      classification: record?.classification || "",
    },
    club: {
      ...emptyRecordDetails.club,
    },
    banking: {
      ...emptyRecordDetails.banking,
    },
  };
}

function displayOrNA(value) {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  return String(value);
}

function displayValue(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
}

function bureauValue(value, fallback = "-") {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (Array.isArray(value)) {
    const values = value.map((item) => bureauValue(item, "")).filter(Boolean);

    return values.length ? values.join(", ") : fallback;
  }

  if (typeof value === "object") {
    return bureauValue(value.value ?? value.label ?? value.name, fallback);
  }

  const stringValue = String(value).trim();

  return stringValue ? stringValue : fallback;
}

function formatIrrPercent(value) {
  const valueText = bureauValue(value, "");

  if (!valueText) {
    return "-";
  }

  if (valueText.endsWith("%")) {
    return valueText;
  }

  const numericValue = Number(valueText.replace(/,/g, ""));

  if (!Number.isFinite(numericValue)) {
    return valueText;
  }

  const percentValue =
    Math.abs(numericValue) <= 1 ? numericValue * 100 : numericValue;

  return `${Number(percentValue.toFixed(2))}%`;
}

function bureauPayloadValue(value) {
  const valueText = bureauValue(value, "");

  if (!valueText || valueText === "-") {
    return "";
  }

  return valueText;
}

function firstBureauPayloadValue(...values) {
  for (const value of values) {
    const normalizedValue = bureauPayloadValue(value);

    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return "";
}

function splitCustomerName(name) {
  const parts = bureauPayloadValue(name)
    .replace(/\b\d{10}\b/g, "")
    .split(/\s+/)
    .filter(Boolean);
  const salutationPattern = /^(mr|mrs|ms|miss|shri|smt)\.?$/i;

  if (parts.length > 1 && salutationPattern.test(parts[0])) {
    parts.shift();
  }

  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

function normalizeBureauDob(value) {
  const rawValue = firstBureauPayloadValue(value);

  if (!rawValue) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
    return rawValue.slice(0, 10);
  }

  const [day, month, year] = rawValue.split(/[-/]/);

  if (day && month && year?.length === 4) {
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  return rawValue;
}

function formatBureauDate(value) {
  const rawValue = bureauPayloadValue(value);

  if (!rawValue) {
    return "-";
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
    return rawValue.slice(0, 10);
  }

  const parsedDate = new Date(rawValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return rawValue;
  }

  return parsedDate.toISOString().slice(0, 10);
}

function bureauTableValue(value) {
  const rawValue = bureauValue(value);

  if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
    return rawValue.slice(0, 10);
  }

  return rawValue;
}

const sectionRiskClasses = {
  neutral: "bg-[#f8f9fa]",
  positive: "bg-[#d1e7dd]",
  risk: "bg-[#fff3cd]",
  negative: "bg-[#f8d7da]",
};

const sectionRiskDotClasses = {
  neutral: "bg-[#adb5bd]",
  positive: "bg-[#20a36a]",
  risk: "bg-[#d99a00]",
  negative: "bg-[#dc3545]",
};

function riskNumber(value) {
  const numericValue = Number(String(value ?? "").replace(/,/g, ""));

  return Number.isFinite(numericValue) ? numericValue : null;
}

function normalizedRiskField(field) {
  return String(field || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_");
}

function isNegativeRiskField(field) {
  const normalizedField = normalizedRiskField(field);

  return ["dpd", "odue", "overdue", "settled", "stld", "woff"].some((term) =>
    normalizedField.includes(term),
  );
}

function isBounceRiskField(field) {
  const normalizedField = normalizedRiskField(field);
  const isCountField = /^(no|num)_/.test(normalizedField);

  return (
    normalizedField.includes("no_bounce") ||
    normalizedField.includes("no_of_bounce") ||
    normalizedField.includes("no_90plus") ||
    normalizedField.includes("no_of_90") ||
    normalizedField.includes("no_180plus") ||
    normalizedField.includes("no_of_180") ||
    (isCountField &&
      ["bounce", "90plus", "90_plus", "180plus", "180_plus"].some((term) =>
        normalizedField.includes(term),
      ))
  );
}

function hasMeaningfulRiskValue(value) {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  const normalizedValue = String(value).trim().toLowerCase();

  return normalizedValue !== "-" && normalizedValue !== "n/a";
}

function evaluateSectionRisk(data) {
  if (!data || typeof data !== "object") {
    return "neutral";
  }

  const entries = Object.entries(data).filter(([, value]) =>
    hasMeaningfulRiskValue(value),
  );

  if (!entries.length) {
    return "neutral";
  }

  for (const [field, value] of entries) {
    const normalizedField = normalizedRiskField(field);
    const numericValue = riskNumber(value);

    if (
      numericValue !== null &&
      numericValue > 0 &&
      (isNegativeRiskField(field) || isBounceRiskField(field))
    ) {
      return "negative";
    }

    if (normalizedField === "score" && numericValue !== null) {
      if (numericValue < 650) {
        return "negative";
      }

      if (numericValue < 700) {
        return "risk";
      }
    }

    if (normalizedField === "phone_count" && numericValue > 1) {
      return "negative";
    }
  }

  return "positive";
}

function riskValueClass(field, value) {
  const normalizedField = normalizedRiskField(field);
  const numericValue = riskNumber(value);

  if (numericValue === null) {
    return "";
  }

  if (normalizedField === "score") {
    if (numericValue < 650) {
      return "bg-[#f8d7da] font-semibold text-[#dc3545]";
    }

    if (numericValue < 750) {
      return "bg-[#fff3cd] font-semibold text-[#d97706]";
    }

    return "bg-[#d1e7dd] font-semibold text-[#0f5132]";
  }

  if (numericValue > 0 && isNegativeRiskField(field)) {
    return "bg-[#f8d7da] font-semibold text-[#dc3545]";
  }

  if (normalizedField === "phone_count") {
    return numericValue > 1
      ? "bg-[#f8d7da] font-semibold text-[#dc3545]"
      : "bg-[#d1e7dd] font-semibold text-[#0f5132]";
  }

  return "";
}

function compactBureauAddress(parts) {
  const seenParts = new Set();

  return parts
    .map(bureauPayloadValue)
    .filter((part) => {
      const normalizedPart = part.toUpperCase();

      if (!part || seenParts.has(normalizedPart)) {
        return false;
      }

      seenParts.add(normalizedPart);
      return true;
    })
    .join(" ");
}

function buildEligibilityBureauPayload(
  record,
  searchData,
  fallbackRecord = {},
) {
  const contact = searchData?.contact || {};
  const formData = searchData?.form_data || {};
  const nameParts = splitCustomerName(
    firstBureauPayloadValue(
      record?.customerName,
      contact.customer_name,
      contact.name,
      formData.customer_name,
      formData.name,
      fallbackRecord.customerName,
    ),
  );

  return {
    phone: firstBureauPayloadValue(
      record?.phone,
      searchData?.phone,
      searchData?.searched_phone,
      contact.phone,
      fallbackRecord.phone,
    ),
    firstName: firstBureauPayloadValue(
      contact.first_name,
      contact.firstName,
      formData.first_name,
      formData.firstName,
      fallbackRecord.firstName,
      nameParts.firstName,
    ),
    lastName: firstBureauPayloadValue(
      contact.last_name,
      contact.lastName,
      formData.last_name,
      formData.lastName,
      fallbackRecord.lastName,
      nameParts.lastName,
    ),
    fatherName: firstBureauPayloadValue(
      contact.father_name,
      contact.fatherName,
      formData.father_name,
      formData.fatherName,
      fallbackRecord.fatherName,
    ),
    dob: normalizeBureauDob(
      firstBureauPayloadValue(
        contact.dob_full,
        contact.dob,
        formData.dob_full,
        formData.dob,
        fallbackRecord.dob,
      ),
    ),
    pan: firstBureauPayloadValue(
      contact.pan_number,
      contact.pan,
      formData.pan_number,
      formData.pan,
      fallbackRecord.panNumber,
      fallbackRecord.pan,
    ).toUpperCase(),
    city: firstBureauPayloadValue(
      record?.city,
      contact.city,
      contact.fd_city,
      formData.city,
      fallbackRecord.city,
    ),
    state: firstBureauPayloadValue(
      record?.state,
      contact.state,
      formData.state,
      fallbackRecord.state,
    ),
    country: firstBureauPayloadValue(
      contact.country,
      formData.country,
      "INDIA",
    ),
    pinCode: firstBureauPayloadValue(
      record?.pincode,
      contact.zip_code,
      contact.pincode,
      contact.pinCode,
      formData.zip_code,
      formData.pincode,
      formData.pinCode,
      fallbackRecord.pincode,
      fallbackRecord.zipCode,
    ),
    address: firstBureauPayloadValue(
      record?.address,
      contact.address,
      formData.address,
      compactBureauAddress([
        record?.house,
        record?.street,
        record?.locality,
        record?.city,
        record?.state,
        record?.pincode,
        contact.house_building_number,
        contact.street_name,
        contact.locality_area,
        contact.city,
        contact.state,
        contact.zip_code,
      ]),
    ),
    productInterested: firstBureauPayloadValue(
      record?.loanProductRaw,
      record?.loanProduct,
      contact.loan_product,
      contact.Loan_Product,
      contact.product_interested,
      formData.loan_product,
      formData.product_interested,
      fallbackRecord.loanProductRaw,
      fallbackRecord.loanProduct,
    ),
  };
}

function objectKeyValueRows(source, preferredKeys = [], columns = 5) {
  if (!source || typeof source !== "object") {
    return [["-"], ["-"]];
  }

  const sourceKeys = Object.keys(source);
  const orderedKeys = [
    ...preferredKeys.filter((key) => sourceKeys.includes(key)),
    ...sourceKeys.filter((key) => !preferredKeys.includes(key)),
  ];
  const rows = [];

  for (let index = 0; index < orderedKeys.length; index += columns) {
    const rowKeys = orderedKeys.slice(index, index + columns);

    rows.push(
      rowKeys,
      rowKeys.map((key) => bureauTableValue(source[key])),
    );
  }

  return rows.length ? rows : [["-"], ["-"]];
}

function buildOfferRows(offer) {
  return [
    ["Amount", "Tenure", "IRR", "EMI", "Score"],
    [
      bureauValue(offer?.preappr_loan_amt),
      bureauValue(offer?.preappr_tenure),
      formatIrrPercent(offer?.preappr_irr),
      bureauValue(offer?.preappr_emi),
      bureauValue(offer?.crif_score),
    ],
  ];
}

function normalizeOffers(offer) {
  if (Array.isArray(offer)) {
    return offer.filter(
      (offerItem) => offerItem && typeof offerItem === "object",
    );
  }

  if (offer && typeof offer === "object" && Object.keys(offer).length) {
    return [offer];
  }

  return [];
}

function buildEnquiryRows(enquiry) {
  if (!enquiry) {
    return enquiryRows;
  }

  return [
    ["Report ID", "Gender", "PAN", "Passport", "UID"],
    [
      bureauValue(enquiry.report_id),
      bureauValue(enquiry.gender),
      bureauValue(enquiry.pan),
      bureauValue(enquiry.passport),
      bureauValue(enquiry.UID),
    ],
    [
      "VOTERID",
      "Ration Card",
      "Address 1 PIN",
      "Address 2 PIN",
      "Address 3 PIN",
    ],
    [
      bureauValue(enquiry.voterid),
      bureauValue(enquiry.rationcard),
      bureauValue(enquiry.address_1_pincode),
      bureauValue(enquiry.address_2_pincode),
      bureauValue(enquiry.address_3_pincode),
    ],
  ];
}

function buildLoanDemographyRows(loan) {
  if (!loan) {
    return loanDemographyRows;
  }

  return [
    ["Report ID", "Name1", "DOB1", "PAN1", "CKYC1"],
    [
      bureauValue(loan.report_id),
      bureauValue(loan.Name_1),
      bureauValue(loan.DOB_1),
      bureauValue(loan.PAN_1),
      bureauValue(loan.CKYC_1),
    ],
    ["Name2", "DOB2", "PAN2", "UID1", "UID2"],
    [
      bureauValue(loan.Name_2),
      bureauValue(loan.DOB_2),
      bureauValue(loan.PAN_2),
      bureauValue(loan.UID_1),
      bureauValue(loan.UID_2),
    ],
    ["DL1", "Passport1", "VOTERID1", "NREGA1", "Ration Card1"],
    [
      bureauValue(loan.DL_1),
      bureauValue(loan.passport_1),
      bureauValue(loan.VOTERID_1),
      bureauValue(loan.NREGA_1),
      bureauValue(loan.Rationcard_1),
    ],
  ];
}

function addressValue(address, pinCode) {
  return compactBureauAddress([address, pinCode]) || "-";
}

function buildEnquiryAddressRows(enquiry) {
  if (!enquiry) {
    return addressRows;
  }

  return [1, 2, 3].map((index) => [
    `Address ${index}`,
    formatBureauDate(enquiry[`address_${index}_reported_date`]),
    "-",
    addressValue(
      enquiry[`address_${index}`],
      enquiry[`address_${index}_pincode`],
    ),
  ]);
}

function buildLoanAddressRows(loan) {
  if (!loan) {
    return addressRows;
  }

  return [1, 2, 3].map((index) => [
    `Address ${index}`,
    formatBureauDate(loan[`Address_${index}_reported_on`]),
    bureauValue(loan[`Address_${index}_reported_prd`]),
    bureauValue(loan[`Address_${index}`]),
  ]);
}

function buildEnquiryPhoneRows(enquiry) {
  if (!enquiry) {
    return phoneRows;
  }

  return [1, 2, 3].map((index) => [
    `Phone ${index}`,
    "-",
    "-",
    bureauValue(enquiry[`phone_${index}`]),
  ]);
}

function buildLoanPhoneRows(loan) {
  if (!loan) {
    return phoneRows;
  }

  return [1, 2, 3].map((index) => [
    `Phone ${index}`,
    formatBureauDate(loan[`contact_${index}_reported_on`]),
    bureauValue(loan[`contact_${index}_reported_prd`]),
    bureauValue(loan[`contact_${index}`]),
  ]);
}

function buildDemographyEmailRows(source) {
  if (!source) {
    return emailRows;
  }

  return [1, 2, 3].map((index) => [
    `Email ${index}`,
    "-",
    "-",
    bureauValue(source[`email_${index}`]),
  ]);
}

function mapEligibilityBureauData(bureauData) {
  const data = bureauData?.data || bureauData || {};
  const codifiedSummary = data.codifiedSummary || data.codified_summary || {};
  const offers = normalizeOffers(data.offer);
  const demography = data.demography || {};
  const enquiry = demography.enquiry || {};
  const loan = demography.loan || {};
  const codifiedOrder = [
    "report_id",
    "report_date",
    "score",
    "ttl_acc",
    "ttl_indv_acc",
    "ttl_indv_act_acc",
    "ttl_indv_clsd_acc",
    "ttl_indv_act_sec_acc",
    "ttl_indv_act_unsec_acc",
    "ttl_indv_clsd_sec_acc",
    "ttl_indv_clsd_unsec_acc",
    "ttl_indv_odue_acc",
    "ttl_indv_woff_acc",
    "ttl_indv_stld_acc",
    "ttl_ind_sanc",
    "ttl_ind_act_sanc",
    "ttl_ind_cur_bal",
    "ttl_ind_clsd_sanc",
    "latest_trade_reported",
    "max_emi_amt",
    "loan_type_input",
  ];

  return {
    codifiedSummaryData: codifiedSummary,
    codifiedSummaryRows: objectKeyValueRows(codifiedSummary, codifiedOrder, 5),
    offers,
    enquiryData: enquiry,
    enquiryRows: buildEnquiryRows(demography.enquiry),
    loanData: loan,
    loanDemographyRows: buildLoanDemographyRows(demography.loan),
    enquiryAddressRows: buildEnquiryAddressRows(enquiry),
    enquiryPhoneRows: buildEnquiryPhoneRows(enquiry),
    enquiryEmailRows: buildDemographyEmailRows(enquiry),
    loanAddressRows: buildLoanAddressRows(loan),
    loanPhoneRows: buildLoanPhoneRows(loan),
    loanEmailRows: buildDemographyEmailRows(loan),
  };
}

function formatLabel(value) {
  return displayValue(value)
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatBoolean(value) {
  if (value === true) {
    return "Yes";
  }

  if (value === false) {
    return "No";
  }

  return "N/A";
}

function normalizeDedupeLookupValue(value) {
  const text = displayValue(value).trim();

  if (!text || text === "-" || text.toUpperCase() === "N/A") {
    return "";
  }

  return text;
}

function dedupeTableValue(value, fallback = "-") {
  const text = normalizeDedupeLookupValue(value);

  return text || fallback;
}

function joinDedupeValues(values) {
  if (!Array.isArray(values)) {
    return "-";
  }

  const joinedValues = values
    .map((value) => normalizeDedupeLookupValue(value))
    .filter(Boolean)
    .join(", ");

  return joinedValues || "-";
}

function getDedupePayload(dedupe) {
  if (dedupe?.data && typeof dedupe.data === "object") {
    return dedupe.data;
  }

  return dedupe;
}

function getPanDedupePhones(panDedupe, phoneDedupe) {
  if (Array.isArray(panDedupe.phones)) {
    return panDedupe.phones;
  }

  const recordPhones = panDedupe.records
    ?.map((record) => record?.phone)
    .filter(Boolean);

  if (recordPhones?.length) {
    return recordPhones;
  }

  return phoneDedupe.phones;
}

function buildLeadApplicationDedupeRows(dedupe) {
  const dedupePayload = getDedupePayload(dedupe);

  if (!dedupePayload) {
    return dedupeRows;
  }

  const phoneDedupe = dedupePayload.phoneDedupe || {};
  const panDedupe = dedupePayload.panDedupe || {};
  const panNumber = panDedupe.inputPan;
  const phoneCount = panDedupe.matchedCount;
  const rows = [
    ["Pan Number", "Phone Count", "Phone Numbers"],
    [
      dedupeTableValue(panNumber),
      dedupeTableValue(phoneCount),
      joinDedupeValues(getPanDedupePhones(panDedupe, phoneDedupe)),
    ],
  ];

  return rows;
}

function buildLeadApplicationDedupeRiskData(dedupe) {
  const dedupePayload = getDedupePayload(dedupe);
  const panDedupe = dedupePayload?.panDedupe || {};

  return {
    pan_number: panDedupe.inputPan,
    phone_count: panDedupe.matchedCount,
  };
}

function getExistingCustomerRecord(dedupe) {
  return getDedupePayload(dedupe)?.existingCustomerDedupe?.record || null;
}

function existingLoanTableValue(key, value) {
  const formattedValue = bureauTableValue(value);

  if (["Loan_acc_no", "Application_no", "Client_no"].includes(key)) {
    return formattedValue.replace(/^'/, "");
  }

  return formattedValue;
}

function buildExistingLoanRows(dedupe) {
  const existingCustomerRecord = getExistingCustomerRecord(dedupe);

  if (!existingCustomerRecord || typeof existingCustomerRecord !== "object") {
    return existingLoanRows;
  }

  const recordKeys = Object.keys(existingCustomerRecord);
  const preferredKeys = Object.keys(existingLoanFieldLabels);
  const orderedKeys = [
    ...preferredKeys.filter((key) => recordKeys.includes(key)),
    ...recordKeys.filter((key) => !preferredKeys.includes(key)),
  ];
  const rows = [];

  for (let index = 0; index < orderedKeys.length; index += 5) {
    const rowKeys = orderedKeys.slice(index, index + 5);

    rows.push(
      rowKeys.map((key) => existingLoanFieldLabels[key] || formatLabel(key)),
      rowKeys.map((key) =>
        existingLoanTableValue(key, existingCustomerRecord[key]),
      ),
    );
  }

  return rows.length ? rows : existingLoanRows;
}

function formatApplicationLabel(value) {
  return displayValue(value)
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatApplicationDateTime(value) {
  const rawValue = firstBureauPayloadValue(value);

  if (!rawValue) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(rawValue)) {
    return rawValue
      .replace("T", " ")
      .replace(/\.\d+Z?$/, "")
      .replace(/Z$/, "")
      .slice(0, 19);
  }

  return rawValue;
}

function objectValue(source, camelKey, snakeKey) {
  const value = source?.[camelKey] || source?.[snakeKey];

  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
}

function applicationLookupData(applicationLookup) {
  const summary = applicationLookup?.summary || {};
  const application = {
    ...summary,
    ...(applicationLookup?.application || {}),
  };
  const applicationId = firstBureauPayloadValue(
    applicationLookup?.applicationId,
    application.id,
    application.applicationId,
    application.application_id,
  );

  return { application, applicationId };
}

function mapEligibilityApplicationInfo(applicationLookup, fallbackInfo = {}) {
  if (!applicationLookup) {
    return fallbackInfo;
  }

  const { application, applicationId } =
    applicationLookupData(applicationLookup);
  const loanApprovalDetails = objectValue(
    application,
    "loanApprovalDetails",
    "loan_approval_details",
  );
  const loanProduct = formatApplicationLabel(
    firstBureauPayloadValue(application.productType, application.product_type),
  );
  const taskType = formatApplicationLabel(
    firstBureauPayloadValue(application.taskType, application.task_type),
  );
  const taskStatus = formatApplicationLabel(
    firstBureauPayloadValue(
      application.taskStatus,
      application.task_status,
      application.state,
      application.status,
    ),
  );
  const assignedTo = firstBureauPayloadValue(
    application.assignedTo,
    application.assigned_to,
    loanApprovalDetails.approvedBy,
    loanApprovalDetails.approved_by,
    application.createdBy,
    application.created_by,
  );
  const appStatus = formatApplicationLabel(
    firstBureauPayloadValue(application.state, application.status),
  );
  const appLastUpdate = formatApplicationDateTime(
    firstBureauPayloadValue(
      application.updatedAt,
      application.updated_at,
      loanApprovalDetails.softApprovalDate,
      loanApprovalDetails.soft_approval_date,
    ),
  );

  return {
    ...fallbackInfo,
    loanProduct: displayOrNA(loanProduct || fallbackInfo.loanProduct),
    taskType: displayOrNA(taskType || fallbackInfo.taskType),
    taskStatus: displayOrNA(taskStatus || fallbackInfo.taskStatus),
    taskAssignedTo: displayOrNA(assignedTo || fallbackInfo.taskAssignedTo),
    appId: displayOrNA(applicationId || fallbackInfo.appId),
    appStatus: displayOrNA(appStatus || fallbackInfo.appStatus),
    appLastUpdate: displayOrNA(appLastUpdate || fallbackInfo.appLastUpdate),
  };
}

function mapEligibilitySearchResponse(data, fallbackRecord = {}) {
  const contact = data?.contact || {};
  const formData = data?.form_data || {};
  const phone = data?.phone || data?.searched_phone || contact.phone;
  const customerName =
    contact.name ||
    contact.customer_name ||
    formData.name ||
    formData.customer_name ||
    fallbackRecord.customerName;
  const loanProduct =
    formatLabel(
      contact.loan_product ||
        contact.Loan_Product ||
        contact.product_interested ||
        formData.loan_product ||
        formData.product_interested,
    ) ||
    fallbackRecord.loanProduct ||
    "";
  const fallbackApplicationInfo = {
    loanProduct: displayOrNA(loanProduct),
    taskType: displayOrNA(contact.task_type || fallbackRecord.taskType),
    taskStatus: displayOrNA(contact.task_status || fallbackRecord.taskStatus),
    taskAssignedTo: displayOrNA(
      contact.task_assigned_to || fallbackRecord.taskAssignedTo,
    ),
    appId: displayOrNA(contact.applicationId || fallbackRecord.appId),
    appStatus: displayOrNA(
      contact.application_status || fallbackRecord.appStatus,
    ),
    appLastUpdate: displayOrNA(
      contact.app_last_update || fallbackRecord.appLastUpdate,
    ),
  };

  return {
    ...fallbackRecord,
    contactId: contact.id || fallbackRecord.contactId,
    rawEligibilityData: data,
    formDataList: data?.form_data_list || [],
    eligibilityProducts: data?.eligibility_products || [],
    dateRequested: fallbackRecord.dateRequested || "",
    customerName: displayValue(customerName),
    phone: displayValue(phone),
    subStatus: displayValue(contact.sub_status || fallbackRecord.subStatus),
    agent: displayValue(
      contact.task_assigned_to || contact.fos_name || fallbackRecord.agent,
    ),
    loanProduct,
    loanProductRaw: displayValue(
      contact.loan_product ||
        contact.Loan_Product ||
        contact.product_interested ||
        formData.loan_product ||
        formData.product_interested ||
        fallbackRecord.loanProductRaw,
    ),
    city: displayValue(contact.city || formData.city || fallbackRecord.city),
    state: displayValue(
      contact.state || formData.state || fallbackRecord.state,
    ),
    pincode: displayValue(
      contact.zip_code ||
        contact.pincode ||
        formData.zip_code ||
        formData.pincode ||
        fallbackRecord.pincode,
    ),
    address: displayValue(
      contact.address || formData.address || fallbackRecord.address,
    ),
    house: displayValue(
      contact.house_building_number ||
        formData.house_building_number ||
        fallbackRecord.house,
    ),
    street: displayValue(
      contact.street_name || formData.street_name || fallbackRecord.street,
    ),
    locality: displayValue(
      contact.locality_area ||
        formData.locality_area ||
        fallbackRecord.locality,
    ),
    panPresent: formatBoolean(contact.doc_pan),
    panNumber: displayOrNA(
      contact.pan_number || contact.pan || formData.pan_number || formData.pan,
    ),
    idProof: displayOrNA(contact.doc_identity),
    dob: displayOrNA(
      contact.dob_full || contact.dob || formData.dob_full || formData.dob,
    ),
    fatherName: displayValue(
      contact.father_name || contact.fatherName || formData.father_name,
    ),
    contactSource: displayOrNA(contact.contact_source),
    sourceDescription: displayOrNA(contact.contact_source_description),
    campaignName: displayOrNA(contact.campaign_name),
    lastCalled: displayOrNA(contact.last_call_date),
    status: displayOrNA(contact.call_status),
    comments: displayOrNA(contact.comments),
    taskType: displayOrNA(contact.task_type),
    taskStatus: displayOrNA(contact.task_status),
    taskAssignedTo: displayOrNA(contact.task_assigned_to),
    appId: displayOrNA(contact.applicationId),
    appStatus: displayOrNA(contact.application_status),
    appLastUpdate: displayOrNA(contact.app_last_update),
    applicationInfo: mapEligibilityApplicationInfo(
      data?.applicationLookup,
      fallbackApplicationInfo,
    ),
    membershipNumber: displayValue(contact.membership_number),
    memberSince: displayValue(contact.member_since),
    memberSinceInput: displayValue(contact.member_since),
  };
}

function mapEligibilityListRow(row) {
  return {
    formDataId: row?.form_data_id,
    contactId: row?.contact_id,
    serialNumber: row?.serial_number,
    dateRequested: displayValue(row?.created_at),
    customerName: displayValue(row?.customer_name),
    phone: displayValue(row?.phone),
    subStatus: displayValue(row?.sub_status),
    agent: displayValue(row?.agent_name || row?.fos_name || row?.agent_id),
    loanProduct:
      formatLabel(row?.product_interested) ||
      displayValue(row?.product_interested),
    city: displayValue(row?.city),
    house: displayValue(row?.house_building_number),
    street: displayValue(row?.street_name),
    locality: displayValue(row?.locality_area),
    state: displayValue(row?.state),
    pincode: displayValue(row?.zip_code),
    contactSource: "N/A",
    sourceDescription: "N/A",
    campaignName: "N/A",
    lastCalled: displayOrNA(row?.followup_date_hist),
    status: displayOrNA(row?.call_status),
    comments: displayOrNA(row?.comments),
    taskAssignedTo: displayOrNA(row?.agent_name || row?.agent_id),
    loanProductRaw: displayValue(row?.product_interested),
  };
}

function formatApiNumber(value, fixedDecimals = false) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const number = Number(value);

  if (Number.isFinite(number)) {
    return fixedDecimals
      ? number.toFixed(2)
      : Number.isInteger(number)
        ? String(number)
        : number.toFixed(2);
  }

  return String(value);
}

function mapPilInfoResponse(response) {
  const data = response?.data || {};
  const memberProfile = data.memberProfile || {};
  const bankingPerformance = data.bankingPerformance || {};
  const clubDetails = data.clubDetails || {};
  const businessDevelopment = data.businessDevelopment || {};
  const contactDetails = data.contactDetails || {};

  return {
    personal: {
      contractorName: displayValue(memberProfile.contractorName),
      address: displayValue(contactDetails.address),
      city: displayValue(contactDetails.city),
      state: displayValue(contactDetails.state),
      pincode: displayValue(contactDetails.pincode),
    },
    club: {
      clubId: displayValue(clubDetails.clubId),
      clubName: displayValue(clubDetails.clubName),
      clubLocation: displayValue(clubDetails.clubLocation),
      bdName: displayValue(businessDevelopment.bdName),
      bdMobile: displayValue(businessDevelopment.bdMobile),
    },
    pil: {
      membershipNumber: displayValue(memberProfile.membershipNumber),
      memberSince: displayValue(memberProfile.memberSince),
      memberSinceInput: displayValue(memberProfile.memberSince),
      classification: displayValue(memberProfile.classification),
    },
    banking: {
      bankingPointsQTD: formatApiNumber(bankingPerformance.pointsQTD, true),
      bankingPointsMTD: formatApiNumber(bankingPerformance.pointsMTD, true),
      bankingQTDKgs: formatApiNumber(bankingPerformance.qtyKgsQTD, true),
      bankingMTDKgs: formatApiNumber(bankingPerformance.qtyKgsMTD, true),
      bankingYTDKgs: formatApiNumber(bankingPerformance.qtyKgsYTD, true),
      bankingYTDPoints: formatApiNumber(bankingPerformance.pointsYTD, true),
      bankingSixMonthPoints: formatApiNumber(
        bankingPerformance.pointsSixMonths,
        true,
      ),
      bankingLast12MonthsPoints: formatApiNumber(
        bankingPerformance.pointsLast12Months,
        true,
      ),
      bankingLast12MonthsKgs: formatApiNumber(
        bankingPerformance.qtyKgsLast12Months,
        true,
      ),
      growthPercentage: formatApiNumber(bankingPerformance.growthPercentage, true),
      loyaltyPoints: formatApiNumber(bankingPerformance.loyaltyPoints),
    },
  };
}

function getPilInfoDetails(record, pilInfoDetails) {
  const recordDetails = getRecordDetails(record);

  return {
    ...recordDetails,
    ...pilInfoDetails,
    personal: {
      ...recordDetails.personal,
      ...pilInfoDetails?.personal,
      contractorName:
        pilInfoDetails?.personal?.contractorName ||
        recordDetails.personal.contractorName,
      city: pilInfoDetails?.personal?.city || recordDetails.personal.city,
    },
    pil: {
      ...recordDetails.pil,
      ...pilInfoDetails?.pil,
    },
    club: {
      ...recordDetails.club,
      ...pilInfoDetails?.club,
    },
    banking: {
      ...recordDetails.banking,
      ...pilInfoDetails?.banking,
    },
  };
}

function memberSinceInputValue(value) {
  if (!value) {
    return "";
  }

  const [year, month, day] = value.split(" ")[0].split("-");

  if (!year || !month || !day) {
    return value;
  }

  return `${day}-${month}-${year}`;
}

function memberDurationText(memberSince) {
  const match = memberSince?.match(/\(([^)]+)\)/);

  return match ? match[1] : "";
}

const tabItems = [
  {
    key: "awaited",
    label: "Awaited",
    icon: <FaRegClock className="h-4 w-4 text-[#7c5d85]" aria-hidden="true" />,
  },
  {
    key: "done",
    label: "Done",
    icon: (
      <FaCheckSquare className="h-4 w-4 text-[#56c986]" aria-hidden="true" />
    ),
  },
  {
    key: "all",
    label: "All",
    icon: (
      <FaClipboardList className="h-4 w-4 text-[#c98655]" aria-hidden="true" />
    ),
  },
];

function TruncatedCell({ value, className = "" }) {
  return (
    <td
      className={`border-r border-t border-[#d9dfe8] px-2 py-2.5 max-[900px]:px-1 ${className}`}
      title={String(value)}
    >
      <span className="block truncate">{value}</span>
    </td>
  );
}

function RecordsTable({ records, searchingPhone, onViewRecord }) {
  return (
    <div className="overflow-x-auto border border-[#d9dfe8]">
      <table className="w-full min-w-[72rem] table-fixed border-collapse text-left text-sm leading-[1.35] min-[1100px]:text-base">
        <colgroup>
          <col className="w-[3%]" />
          <col className="w-[16%]" />
          <col className="w-[22%]" />
          <col className="w-[10%]" />
          <col className="w-[15%]" />
          <col className="w-[11%]" />
          <col className="w-[10%]" />
          <col className="w-[8%]" />
          <col className="w-[5%]" />
        </colgroup>
        <thead>
          <tr className="bg-[#f8f9fa]">
            {[
              "#",
              "Date Requested",
              "Customer Name",
              "Phone",
              "Sub-Status",
              "Agent",
              "Loan Product",
              "City",
            ].map((heading) => (
              <th
                className="border-b border-r border-[#d9dfe8] px-2 py-2 font-bold max-[900px]:px-1"
                key={heading}
              >
                <span className="block truncate" title={heading}>
                  {heading}
                </span>
              </th>
            ))}
            <th className="border-b border-[#d9dfe8] px-1 py-2 text-center font-bold">
              <FaSearch
                className="mx-auto h-4 w-4 text-[#1c9de8]"
                aria-label="Actions"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {records.map((record, index) => (
            <tr
              key={
                record.formDataId ||
                record.contactId ||
                `${record.phone}-${record.dateRequested}`
              }
            >
              <TruncatedCell value={record.serialNumber || index + 1} />
              <TruncatedCell value={record.dateRequested} />
              <TruncatedCell value={record.customerName} />
              <TruncatedCell value={record.phone} />
              <TruncatedCell value={record.subStatus} />
              <TruncatedCell value={record.agent} />
              <TruncatedCell value={record.loanProduct} />
              <TruncatedCell value={record.city} />
              <td className="border-t border-[#d9dfe8] px-1 py-1.5 text-center">
                <button
                  className="inline-flex h-[34px] w-full max-w-[44px] min-w-0 items-center justify-center rounded border border-[#111827] bg-white text-[#1c9de8] transition hover:bg-[#eef6ff] focus-visible:bg-[#eef6ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9fc8ff] disabled:cursor-not-allowed disabled:opacity-60"
                  type="button"
                  aria-label={`View ${record.customerName}`}
                  aria-busy={searchingPhone === record.phone}
                  disabled={Boolean(searchingPhone)}
                  onClick={() => onViewRecord(record)}
                >
                  <FaSearch className="h-4 w-4" aria-hidden="true" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DetailField({ label, value }) {
  return (
    <div className="min-w-0">
      <dt className="mb-1 text-sm font-bold">{label}</dt>
      <dd className="m-0 truncate text-sm leading-[1.45]" title={String(value)}>
        {value || "-"}
      </dd>
    </div>
  );
}

function DetailPanel({
  icon,
  title,
  titleClassName = "text-[#0d73ff]",
  children,
}) {
  return (
    <section className="rounded-[0.5rem] border border-[#d1d5db] bg-white px-4 py-4">
      <h3
        className={`mb-4 flex items-center gap-2 text-base font-semibold ${titleClassName}`}
      >
        {icon}
        {title}
      </h3>
      {children}
    </section>
  );
}

function CompactTable({ rows, columns = 3, cellClassName }) {
  return (
    <div className="overflow-x-auto border border-[#d9dfe8]">
      <table className="w-full min-w-[42rem] table-fixed border-collapse text-xs leading-[1.35]">
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`${row.join("-")}-${rowIndex}`}>
              {Array.from({ length: columns }).map((_, columnIndex) => {
                const value = row[columnIndex] || "-";
                const isLabelCell = rowIndex % 2 === 0;
                const riskClassName =
                  cellClassName?.({
                    columnIndex,
                    row,
                    rowIndex,
                    rows,
                    value,
                  }) || "";
                const backgroundClassName = riskClassName
                  ? ""
                  : isLabelCell
                    ? "bg-[#e9ecef] font-semibold"
                    : "bg-white";

                return (
                  <td
                    className={`border border-[#d9dfe8] px-2 py-2 text-center ${backgroundClassName} ${riskClassName}`}
                    key={`${value}-${columnIndex}`}
                    title={value}
                  >
                    <span className="block truncate">{value}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SummarySection({ icon, title, children, risk = "neutral" }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const headerRiskClass =
    sectionRiskClasses[risk] || sectionRiskClasses.neutral;
  const dotRiskClass =
    sectionRiskDotClasses[risk] || sectionRiskDotClasses.neutral;

  return (
    <section className="rounded-[0.5rem] border border-[#d1d5db] bg-white">
      <button
        className={`flex h-10 w-full cursor-pointer items-center justify-between rounded-t-[0.5rem] px-4 text-left text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#c7b7ff] ${headerRiskClass} ${
          isExpanded ? "border-b border-[#d9dfe8]" : ""
        }`}
        type="button"
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${title}`}
        onClick={() => setIsExpanded((currentValue) => !currentValue)}
      >
        <span className="flex items-center gap-2">
          <span
            className={`h-3.5 w-3.5 flex-none rounded-full shadow-[inset_0_1px_2px_rgba(255,255,255,0.65),0_1px_2px_rgba(15,23,42,0.2)] ${dotRiskClass}`}
            aria-hidden="true"
          />
          {icon}
          {title}
        </span>
        <span className="inline-flex h-8 w-8 items-center justify-center text-[#6d4b8a]">
          {isExpanded ? (
            <FaMinus className="h-4 w-4" aria-hidden="true" />
          ) : (
            <FaPlus className="h-4 w-4" aria-hidden="true" />
          )}
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
          isExpanded
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="p-4">{children}</div>
        </div>
      </div>
    </section>
  );
}

function SubTable({ title, icon, rows }) {
  return (
    <div className="mt-4">
      <h4 className="mb-2 flex items-center gap-2 text-sm font-bold">
        {icon}
        {title}
      </h4>
      <CompactTable
        columns={4}
        rows={[["Type", "Reported On", "Product", "Value"], ...rows]}
      />
    </div>
  );
}

function EligibilityResultTable({ offers, fallbackProduct }) {
  const visibleOffers = normalizeOffers(offers);
  const offerItems = visibleOffers.length ? visibleOffers : [{}];

  return (
    <div className="space-y-4">
      {offerItems.map((offer, index) => {
        const productName =
          bureauValue(offer.loan_type_input, "") ||
          bureauValue(fallbackProduct, "") ||
          "-";

        return (
          <div
            className={index ? "border-t border-[#d9dfe8] pt-4" : ""}
            key={offer.id || `${offer.report_id || "offer"}-${index}`}
          >
            <p className="mb-2 text-sm font-bold text-[#0066ff]">
              {productName}:
            </p>
            <CompactTable columns={5} rows={buildOfferRows(offer)} />
            <p className="mt-2 text-sm">
              <span className="font-bold">Message:</span>{" "}
              {bureauValue(offer.decision_reason)}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function AnimatedModal({ ariaLabelledBy, children, onClose }) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => setIsOpen(true));

    return () => cancelAnimationFrame(frameId);
  }, []);

  const requestClose = () => {
    setIsOpen(false);
    window.setTimeout(onClose, 180);
  };

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/45 px-4 py-6 transition-opacity duration-200 ${
        isOpen ? "opacity-100" : "opacity-0"
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
      onMouseDown={requestClose}
    >
      <section
        className={`flex max-h-[88vh] w-full max-w-[96rem] flex-col overflow-hidden rounded-[0.5rem] bg-white shadow-[0_20px_70px_rgba(15,23,42,0.35)] transition duration-200 ${
          isOpen
            ? "translate-y-0 scale-100 opacity-100"
            : "translate-y-2 scale-[0.98] opacity-0"
        }`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {children(requestClose)}
      </section>
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <div className="text-base leading-[1.45]">
      <span className="font-bold">{label}:</span>
      {value ? <span> {value}</span> : null}
    </div>
  );
}

function PilInfoCard({
  className = "",
  icon,
  rows,
  rowsClassName = "",
  title,
}) {
  return (
    <section
      className={`rounded-[0.5rem] border border-[#d1d5db] bg-white px-4 py-5 shadow-[0_2px_8px_rgba(15,23,42,0.12)] ${className}`}
    >
      <h3 className="mb-5 flex items-center gap-2 border-b border-[#d9d9d9] pb-2 text-base font-bold text-[#111827]">
        {icon}
        {title}
      </h3>
      <div className={`grid gap-x-8 gap-y-3 ${rowsClassName}`}>
        {rows.map(([label, value]) => (
          <InfoLine key={label} label={label} value={value} />
        ))}
      </div>
    </section>
  );
}

function PilInfoModal({ pilInfoDetails, record, onClose }) {
  const details = getPilInfoDetails(record, pilInfoDetails);
  const pilCards = [
    {
      title: "Personal Info",
      icon: (
        <FaUserTie
          className="h-4 w-4 flex-none text-[#432071]"
          aria-hidden="true"
        />
      ),
      rows: [
        ["Contractor Name", details.personal.contractorName],
        ["Address", details.personal.address],
        ["City", details.personal.city],
        ["State", details.personal.state],
        ["PIN Code", details.personal.pincode],
      ],
    },
    {
      title: "Club Info",
      icon: (
        <FaBuilding
          className="h-4 w-4 flex-none text-[#4f8fc8]"
          aria-hidden="true"
        />
      ),
      rows: [
        ["Club ID", details.club.clubId],
        ["Club Name", details.club.clubName],
        ["Club Location", details.club.clubLocation],
        ["BD Name", details.club.bdName],
        ["BD Mobile", details.club.bdMobile],
      ],
    },
    {
      title: "PIL Info",
      icon: (
        <FaFileAlt
          className="h-4 w-4 flex-none text-[#b7a7d8]"
          aria-hidden="true"
        />
      ),
      rows: [
        ["Membership No", details.pil.membershipNumber],
        ["Member Since", details.pil.memberSince],
        ["Classification", details.pil.classification],
      ],
      className: "col-span-2 max-[900px]:col-span-1",
      rowsClassName: "grid-cols-3 max-[900px]:grid-cols-1",
    },
    {
      title: "PIL Banking Info",
      icon: (
        <FaUniversity
          className="h-4 w-4 flex-none text-[#8d819f]"
          aria-hidden="true"
        />
      ),
      rows: [
        ["Banking Points QTD", details.banking.bankingPointsQTD],
        ["Banking Points MTD", details.banking.bankingPointsMTD],
        ["Banking QTD (kg)", details.banking.bankingQTDKgs],
        ["Banking MTD (kg)", details.banking.bankingMTDKgs],
        ["Banking YTD (kg)", details.banking.bankingYTDKgs],
        ["Banking YTD Points", details.banking.bankingYTDPoints],
        ["Banking Six Month Points", details.banking.bankingSixMonthPoints],
        [
          "Banking Last 12 Months Points",
          details.banking.bankingLast12MonthsPoints,
        ],
        ["Banking Last 12 Months", details.banking.bankingLast12MonthsKgs],
        ["Growth Percentage", details.banking.growthPercentage],
        ["Loyalty Points", details.banking.loyaltyPoints],
      ],
      className: "col-span-2 max-[900px]:col-span-1",
      rowsClassName: "grid-cols-2 max-[720px]:grid-cols-1",
    },
  ];

  return (
    <AnimatedModal ariaLabelledBy="pil-info-title" onClose={onClose}>
      {(requestClose) => (
        <>
          <div className="flex h-[58px] flex-none items-center justify-end border-b border-[#d9dfe8] px-4">
            <button
              className="inline-flex h-10 w-10 items-center justify-center rounded border border-transparent text-[#6b7280] transition hover:bg-[#f3f4f6] focus-visible:bg-[#f3f4f6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9fc8ff]"
              type="button"
              aria-label="Close PIL info"
              onClick={requestClose}
            >
              <FaTimes className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="app-scrollbar overflow-y-auto px-[clamp(1rem,4vw,3rem)] py-8">
            <h2
              id="pil-info-title"
              className="mb-8 flex items-center gap-3 text-[1.75rem] font-bold text-[#171717] max-[720px]:text-2xl"
            >
              <FaClipboardList
                className="h-8 w-8 flex-none text-[#c98655]"
                aria-hidden="true"
              />
              PIL Info for {record.phone}
            </h2>

            <div className="grid grid-cols-2 gap-6 max-[900px]:grid-cols-1">
              {pilCards.map((card) => (
                <PilInfoCard
                  icon={card.icon}
                  key={card.title}
                  rows={card.rows}
                  className={card.className}
                  rowsClassName={card.rowsClassName}
                  title={card.title}
                />
              ))}
            </div>

            <div className="flex justify-center pt-10">
              <button
                className="inline-flex h-[38px] items-center justify-center gap-2 rounded-md border border-[#dc3545] bg-white px-5 text-base font-medium text-[#dc3545] transition hover:bg-[#fff5f5] focus-visible:bg-[#fff5f5]"
                type="button"
                onClick={requestClose}
              >
                <FaTimes className="h-4 w-4" aria-hidden="true" />
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </AnimatedModal>
  );
}

function ResultField({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-base">{label}</span>
      {children}
    </label>
  );
}

function EligibilityResultModal({
  record,
  resultForm,
  resultOptions,
  onClose,
  onSave,
}) {
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedSubStatus, setSelectedSubStatus] = useState("");
  const [comments, setComments] = useState("");
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const details = getRecordDetails(record);
  const memberInfo = resultForm?.member_info || {};
  const formOptions =
    resultOptions?.form_options || resultForm?.form_options || {};
  const subStatusOptionsByStatus =
    resultOptions?.status_sub_status_options ||
    formOptions.sub_status_options ||
    {};
  const statusOptions = formOptions.selectable_statuses?.length
    ? formOptions.selectable_statuses
    : formOptions.statuses || Object.keys(subStatusOptionsByStatus);
  const subStatusOptions = selectedStatus
    ? subStatusOptionsByStatus[selectedStatus] || []
    : [];
  const memberSince =
    displayValue(memberInfo.member_since_date) ||
    displayValue(memberInfo.member_since) ||
    details.pil.memberSinceInput ||
    details.pil.memberSince;
  const durationText =
    displayValue(memberInfo.member_age_text) || memberDurationText(memberSince);
  const membershipNo =
    displayValue(memberInfo.membership_number) || details.pil.membershipNumber;
  const classification =
    displayValue(memberInfo.contractor_classification) ||
    details.pil.classification;
  const inputValueClass = `${inputClass} text-[#111827]`;

  return (
    <AnimatedModal ariaLabelledBy="eligibility-result-title" onClose={onClose}>
      {(requestClose) => {
        const handleSave = async (event) => {
          event.preventDefault();

          if (!selectedStatus || !selectedSubStatus) {
            setSaveError("Please select status and sub-status.");
            return;
          }

          setSaveError("");
          setIsSaving(true);

          try {
            await onSave({
              contact_id: record.contactId,
              phone: record.phone,
              membership_no: membershipNo,
              member_since: memberSince,
              classification,
              call_status: selectedStatus,
              sub_status: selectedSubStatus,
              comments,
            });
            requestClose();
          } catch (error) {
            setSaveError(error.message);
          } finally {
            setIsSaving(false);
          }
        };

        return (
          <>
            <div className="flex h-[58px] flex-none items-center justify-between border-b border-[#d9dfe8] px-4">
              <h2
                id="eligibility-result-title"
                className="m-0 flex items-center gap-3 text-[1.35rem] font-bold text-[#171717] max-[720px]:text-xl"
              >
                <FaClipboardList
                  className="h-6 w-6 flex-none text-[#c98655]"
                  aria-hidden="true"
                />
                Eligibility Check Result Entry
              </h2>
              <button
                className="inline-flex h-10 w-10 items-center justify-center rounded border border-transparent text-[#6b7280] transition hover:bg-[#f3f4f6] focus-visible:bg-[#f3f4f6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9fc8ff]"
                type="button"
                aria-label="Close eligibility result"
                onClick={requestClose}
              >
                <FaTimes className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <form
              className="app-scrollbar overflow-y-auto px-[clamp(1rem,4vw,3rem)] py-8"
              onSubmit={handleSave}
            >
              <div className="grid grid-cols-3 gap-x-6 gap-y-5 max-[980px]:grid-cols-1">
                <ResultField label="Customer Name">
                  <input
                    className={inputValueClass}
                    readOnly
                    type="text"
                    value={
                      displayValue(memberInfo.name) ||
                      details.personal.contractorName
                    }
                  />
                </ResultField>

                <ResultField label="Phone">
                  <input
                    className={inputValueClass}
                    readOnly
                    type="text"
                    value={record.phone}
                  />
                </ResultField>

                <ResultField label="City">
                  <input
                    className={inputValueClass}
                    readOnly
                    type="text"
                    value={
                      displayValue(memberInfo.city) || details.personal.city
                    }
                  />
                </ResultField>

                <ResultField label="Membership No">
                  <input
                    className={inputValueClass}
                    readOnly
                    type="text"
                    value={membershipNo}
                  />
                </ResultField>

                <ResultField label="Membership Since">
                  <input
                    className={inputValueClass}
                    readOnly
                    type="text"
                    value={memberSinceInputValue(memberSince)}
                  />
                  {durationText ? (
                    <p className="mt-2 flex items-center gap-2 text-sm text-[#5f6368]">
                      <FaRegClock
                        className="h-4 w-4 text-[#7c5d85]"
                        aria-hidden="true"
                      />
                      Member for{" "}
                      <span className="font-bold">{durationText}</span>
                    </p>
                  ) : null}
                </ResultField>

                <ResultField label="Classification">
                  <input
                    className={inputValueClass}
                    readOnly
                    type="text"
                    value={classification}
                  />
                </ResultField>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5 max-[980px]:grid-cols-1">
                <ResultField label="Status *">
                  <select
                    className={inputClass}
                    value={selectedStatus}
                    onChange={(event) => {
                      setSelectedStatus(event.target.value);
                      setSelectedSubStatus("");
                    }}
                  >
                    <option value="">-- Select Status --</option>
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </ResultField>

                <ResultField label="Sub-Status *">
                  <select
                    className={inputClass}
                    value={selectedSubStatus}
                    disabled={!selectedStatus}
                    onChange={(event) =>
                      setSelectedSubStatus(event.target.value)
                    }
                  >
                    <option value="">-- Select Sub-Status --</option>
                    {subStatusOptions.map((subStatus) => (
                      <option key={subStatus} value={subStatus}>
                        {subStatus}
                      </option>
                    ))}
                  </select>
                </ResultField>
              </div>

              <div className="mt-6">
                <ResultField label="Comments">
                  <textarea
                    className="min-h-[126px] w-full rounded-md border border-[#d8dee6] bg-white px-3 py-2 text-base outline-none transition placeholder:text-[#6b7280] focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.18)]"
                    placeholder="Any remarks..."
                    value={comments}
                    onChange={(event) => setComments(event.target.value)}
                  />
                </ResultField>
              </div>

              {saveError ? (
                <div
                  className="mt-5 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-sm text-[#842029]"
                  role="alert"
                >
                  {saveError}
                </div>
              ) : null}

              <div className="mt-6 flex justify-end gap-2">
                <button
                  className="inline-flex h-[38px] min-w-[6rem] items-center justify-center gap-2 rounded-md bg-[#16834a] px-4 text-base font-medium text-white transition hover:bg-[#126d3d] focus-visible:bg-[#126d3d] disabled:cursor-not-allowed disabled:opacity-70"
                  type="submit"
                  disabled={isSaving}
                >
                  <FaSave className="h-4 w-4" aria-hidden="true" />
                  {isSaving ? "Saving..." : "Save"}
                </button>
                <button
                  className="inline-flex h-[38px] items-center justify-center rounded-md bg-[#6c757d] px-4 text-base font-medium text-white transition hover:bg-[#5c656d] focus-visible:bg-[#5c656d]"
                  type="button"
                  onClick={requestClose}
                >
                  Back
                </button>
              </div>
            </form>
          </>
        );
      }}
    </AnimatedModal>
  );
}

function EligibilityDetailView({
  eligibilityResultError,
  isPilInfoLoading,
  isEligibilityResultLoading,
  pilInfoError,
  record,
  onOpenEligibilityResult,
  onOpenPilInfo,
}) {
  const details = getRecordDetails(record);
  const bureau = record.bureau || {};
  const applicationInfo = record.applicationInfo || {};
  const dedupeSummaryRows = buildLeadApplicationDedupeRows(record.dedupe);
  const dedupeRiskData = buildLeadApplicationDedupeRiskData(record.dedupe);
  const existingCustomerRecord = getExistingCustomerRecord(record.dedupe);
  const existingLoanSummaryRows = buildExistingLoanRows(record.dedupe);

  return (
    <section className="mt-6" aria-label="Eligibility detail">
      <div className="grid grid-cols-3 gap-4 max-[1100px]:grid-cols-1">
        <DetailPanel
          icon={
            <FaUserTie className="h-4 w-4 text-[#e9a03d]" aria-hidden="true" />
          }
          title="Personal Info"
        >
          <dl className="grid grid-cols-3 gap-x-5 gap-y-4 max-[720px]:grid-cols-1">
            <DetailField
              label="Name:"
              value={details.personal.contractorName}
            />
            <DetailField label="Phone:" value={record.phone} />
            <DetailField label="City:" value={details.personal.city} />
            <DetailField label="House" value={record.house} />
            <DetailField label="Street" value={record.street} />
            <DetailField label="Locality" value={record.locality} />
          </dl>

          <h4 className="mb-4 mt-5 flex items-center gap-2 border-t border-[#d9dfe8] pt-4 text-base font-semibold text-[#ffb000]">
            <FaFolderOpen className="h-4 w-4" aria-hidden="true" />
            Document Info
          </h4>
          <dl className="grid grid-cols-2 gap-x-7 gap-y-4 max-[720px]:grid-cols-1">
            <DetailField label="PAN Present:" value={record.panPresent} />
            <DetailField label="PAN Number:" value={record.panNumber} />
            <DetailField label="ID Proof:" value={record.idProof} />
            <DetailField label="DOB:" value={record.dob} />
          </dl>

          <h4 className="mb-4 mt-5 flex items-center gap-2 border-t border-[#d9dfe8] pt-4 text-base font-semibold text-[#ffb000]">
            <FaFileAlt className="h-4 w-4" aria-hidden="true" />
            PIL Info
          </h4>
          <dl className="grid gap-2">
            <DetailField
              label="Membership No:"
              value={details.pil.membershipNumber}
            />
            <DetailField
              label="Member Since:"
              value={details.pil.memberSince}
            />
            <DetailField
              label="Member Classification:"
              value={details.pil.classification}
            />
          </dl>
        </DetailPanel>

        <DetailPanel
          icon={
            <FaPhoneAlt className="h-4 w-4 text-[#d63384]" aria-hidden="true" />
          }
          title="Communication (Contractor)"
          titleClassName="text-[#16834a]"
        >
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 max-[720px]:grid-cols-1">
            <DetailField label="Campaign Name:" value={record.campaignName} />
            <DetailField label="Contact Source:" value={record.contactSource} />
            <DetailField
              label="Source Desc:"
              value={record.sourceDescription}
            />
            <DetailField label="Last Called:" value={record.lastCalled} />
            <DetailField label="Status:" value={record.status} />
            <DetailField
              label="Sub-Status:"
              value={displayOrNA(record.subStatus)}
            />
            <DetailField
              label="Assigned To:"
              value={displayOrNA(record.agent)}
            />
            <DetailField label="Comments:" value={record.comments} />
          </dl>
        </DetailPanel>

        <DetailPanel
          icon={
            <FaClipboardList
              className="h-4 w-4 text-[#d58b63]"
              aria-hidden="true"
            />
          }
          title="Application Info"
          titleClassName="text-[#dc3545]"
        >
          <dl className="grid grid-cols-3 gap-x-5 gap-y-4 max-[720px]:grid-cols-1">
            <DetailField
              label="Loan Product:"
              value={
                applicationInfo.loanProduct || displayOrNA(record.loanProduct)
              }
            />
            <DetailField
              label="Task Type:"
              value={applicationInfo.taskType || record.taskType}
            />
            <DetailField
              label="Task Status:"
              value={applicationInfo.taskStatus || record.taskStatus}
            />
            <DetailField
              label="Assigned To:"
              value={applicationInfo.taskAssignedTo || record.taskAssignedTo}
            />
            <DetailField
              label="App ID:"
              value={applicationInfo.appId || record.appId}
            />
            <DetailField
              label="App Status:"
              value={applicationInfo.appStatus || record.appStatus}
            />
            <DetailField
              label="App Last Update:"
              value={applicationInfo.appLastUpdate || record.appLastUpdate}
            />
          </dl>

          <h4 className="mb-4 mt-5 flex items-center gap-2 border-t border-[#d9dfe8] pt-4 text-base font-semibold text-[#ffb000]">
            <FaClipboardList className="h-4 w-4" aria-hidden="true" />
            Eligibility Info
          </h4>
          <EligibilityResultTable
            fallbackProduct={record.loanProduct}
            offers={bureau.offers}
          />
        </DetailPanel>
      </div>

      <div className="mt-5 grid gap-5">
        <SummarySection
          icon={
            <FaIdCard className="h-4 w-4 text-[#dc3545]" aria-hidden="true" />
          }
          title="Bureau Codified Summary"
          risk={evaluateSectionRisk(bureau.codifiedSummaryData)}
        >
          <CompactTable
            columns={5}
            rows={bureau.codifiedSummaryRows || summaryRows}
            cellClassName={({ columnIndex, rowIndex, rows, value }) =>
              rowIndex % 2 === 1
                ? riskValueClass(rows[rowIndex - 1]?.[columnIndex], value)
                : ""
            }
          />
        </SummarySection>

        <SummarySection
          icon={
            <FaPhoneAlt className="h-4 w-4 text-[#d63384]" aria-hidden="true" />
          }
          title="Lead/Application Dedupe Summary"
          risk={evaluateSectionRisk(dedupeRiskData)}
        >
          <CompactTable
            columns={3}
            rows={dedupeSummaryRows}
            cellClassName={({ columnIndex, rowIndex, value }) =>
              rowIndex === 1 && columnIndex === 1
                ? riskValueClass("phone_count", value)
                : ""
            }
          />
        </SummarySection>

        <SummarySection
          icon={
            <FaFileAlt className="h-4 w-4 text-[#6d4b8a]" aria-hidden="true" />
          }
          title="Pargro-existing Loan Summary"
          risk={evaluateSectionRisk(existingCustomerRecord)}
        >
          <CompactTable
            columns={5}
            rows={existingLoanSummaryRows}
            cellClassName={({ columnIndex, rowIndex, rows, value }) =>
              rowIndex % 2 === 1
                ? riskValueClass(rows[rowIndex - 1]?.[columnIndex], value)
                : ""
            }
          />
        </SummarySection>

        <SummarySection
          icon={
            <FaFileAlt className="h-4 w-4 text-[#22c55e]" aria-hidden="true" />
          }
          title="Bureau Enquiry Demography"
          risk={evaluateSectionRisk(bureau.enquiryData)}
        >
          <CompactTable columns={5} rows={bureau.enquiryRows || enquiryRows} />
          <SubTable
            icon={
              <FaIdCard className="h-4 w-4 text-[#d63384]" aria-hidden="true" />
            }
            title="Addresses"
            rows={bureau.enquiryAddressRows || addressRows}
          />
          <SubTable
            icon={
              <FaPhoneAlt
                className="h-4 w-4 text-[#d63384]"
                aria-hidden="true"
              />
            }
            title="Phones"
            rows={bureau.enquiryPhoneRows || phoneRows}
          />
          <SubTable
            icon={
              <FaIdCard className="h-4 w-4 text-[#7c5dff]" aria-hidden="true" />
            }
            title="Emails"
            rows={bureau.enquiryEmailRows || emailRows}
          />
        </SummarySection>

        <SummarySection
          icon={
            <FaFileAlt className="h-4 w-4 text-[#22c55e]" aria-hidden="true" />
          }
          title="Bureau Loan Demography"
          risk={evaluateSectionRisk(bureau.loanData)}
        >
          <CompactTable
            columns={5}
            rows={bureau.loanDemographyRows || loanDemographyRows}
          />
          <SubTable
            icon={
              <FaIdCard className="h-4 w-4 text-[#d63384]" aria-hidden="true" />
            }
            title="Addresses"
            rows={bureau.loanAddressRows || addressRows}
          />
          <SubTable
            icon={
              <FaPhoneAlt
                className="h-4 w-4 text-[#d63384]"
                aria-hidden="true"
              />
            }
            title="Phones"
            rows={bureau.loanPhoneRows || phoneRows}
          />
          <SubTable
            icon={
              <FaIdCard className="h-4 w-4 text-[#7c5dff]" aria-hidden="true" />
            }
            title="Emails"
            rows={bureau.loanEmailRows || emailRows}
          />
        </SummarySection>
      </div>

      <div className="mt-5 flex flex-wrap justify-end gap-3">
        <button
          className="inline-flex h-[38px] min-w-[11.75rem] items-center justify-center gap-2 rounded-md border border-[#06b6d4] bg-white px-4 text-base font-medium text-[#0788a4] transition hover:bg-[#ecfeff] focus-visible:bg-[#ecfeff] disabled:cursor-not-allowed disabled:opacity-70"
          type="button"
          aria-busy={isPilInfoLoading}
          disabled={isPilInfoLoading}
          onClick={onOpenPilInfo}
        >
          <FaSearch className="h-4 w-4" aria-hidden="true" />
          {isPilInfoLoading ? "Fetching Info..." : "PIL Info View"}
        </button>
        <button
          className="inline-flex h-[38px] min-w-[12rem] items-center justify-center gap-2 rounded-md bg-[#16834a] px-4 text-base font-medium text-white transition hover:bg-[#126d3d] focus-visible:bg-[#126d3d] disabled:cursor-not-allowed disabled:opacity-70"
          type="button"
          aria-busy={isEligibilityResultLoading}
          disabled={isEligibilityResultLoading}
          onClick={onOpenEligibilityResult}
        >
          <FaClipboardList className="h-4 w-4" aria-hidden="true" />
          {isEligibilityResultLoading
            ? "Fetching Result..."
            : "Eligibility Result"}
        </button>
      </div>

      {pilInfoError ? (
        <div
          className="mt-3 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-sm text-[#842029]"
          role="alert"
        >
          {pilInfoError}
        </div>
      ) : null}

      {eligibilityResultError ? (
        <div
          className="mt-3 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-sm text-[#842029]"
          role="alert"
        >
          {eligibilityResultError}
        </div>
      ) : null}
    </section>
  );
}

function EligibilityCheckPage() {
  const [activeTab, setActiveTab] = useState("awaited");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isEligibilityResultOpen, setIsEligibilityResultOpen] = useState(false);
  const [isPilInfoOpen, setIsPilInfoOpen] = useState(false);
  const [pilInfoDetails, setPilInfoDetails] = useState(null);
  const [pilInfoError, setPilInfoError] = useState("");
  const [isPilInfoLoading, setIsPilInfoLoading] = useState(false);
  const [eligibilityResultForm, setEligibilityResultForm] = useState(null);
  const [eligibilityResultOptions, setEligibilityResultOptions] =
    useState(null);
  const [eligibilityResultError, setEligibilityResultError] = useState("");
  const [isEligibilityResultLoading, setIsEligibilityResultLoading] =
    useState(false);
  const [eligibilitySearchError, setEligibilitySearchError] = useState("");
  const [eligibilitySearchPhone, setEligibilitySearchPhone] = useState("");
  const [eligibilityRows, setEligibilityRows] = useState([]);
  const [eligibilityListTitle, setEligibilityListTitle] = useState("");
  const [eligibilityListTotal, setEligibilityListTotal] = useState(0);
  const [eligibilityListError, setEligibilityListError] = useState("");
  const [isEligibilityListLoading, setIsEligibilityListLoading] =
    useState(false);
  const [eligibilityListPage, setEligibilityListPage] = useState(1);
  const [eligibilityListReloadKey, setEligibilityListReloadKey] = useState(0);
  const [eligibilityPagination, setEligibilityPagination] = useState({
    enabled: false,
    totalPages: 1,
  });
  const activeRecords = eligibilityRows;
  const activeTabLabel =
    tabItems.find((tab) => tab.key === activeTab)?.label || "Done";

  usePageTitle("Eligibility Check | Pargro");

  useEffect(() => {
    let isCurrentRequest = true;

    const loadEligibilityList = async () => {
      setIsEligibilityListLoading(true);
      setEligibilityListError("");

      try {
        const response = await fetchEligibilityCheckList({
          filter: activeTab,
          page: eligibilityListPage,
          limit: eligibilityListLimit,
        });

        if (!isCurrentRequest) {
          return;
        }

        setEligibilityRows((response?.rows || []).map(mapEligibilityListRow));
        setEligibilityListTitle(response?.title || "");
        setEligibilityListTotal(response?.total || 0);
        setEligibilityPagination({
          enabled: Boolean(response?.pagination?.enabled),
          totalPages: response?.pagination?.total_pages || 1,
        });
      } catch (error) {
        if (!isCurrentRequest) {
          return;
        }

        setEligibilityRows([]);
        setEligibilityListTitle("");
        setEligibilityListTotal(0);
        setEligibilityPagination({
          enabled: false,
          totalPages: 1,
        });
        setEligibilityListError(error.message);
      } finally {
        if (isCurrentRequest) {
          setIsEligibilityListLoading(false);
        }
      }
    };

    loadEligibilityList();

    return () => {
      isCurrentRequest = false;
    };
  }, [activeTab, eligibilityListPage, eligibilityListReloadKey]);

  const resetPilInfoState = () => {
    setPilInfoDetails(null);
    setPilInfoError("");
    setIsPilInfoLoading(false);
  };

  const resetEligibilityResultState = () => {
    setEligibilityResultForm(null);
    setEligibilityResultOptions(null);
    setEligibilityResultError("");
    setIsEligibilityResultLoading(false);
  };

  const searchEligibilityDetails = async (phone, fallbackRecord = {}) => {
    const selectedPhone = String(phone || "").trim();

    if (!selectedPhone) {
      setEligibilitySearchError("Please enter a phone number.");
      return;
    }

    setEligibilitySearchError("");
    setEligibilitySearchPhone(selectedPhone);
    setIsEligibilityResultOpen(false);
    setIsPilInfoOpen(false);
    resetPilInfoState();
    resetEligibilityResultState();

    try {
      const [applicationLookup, eligibilityLookup] = await Promise.all([
        fetchApplicationByLeadId(selectedPhone)
          .then((applicationData) => ({ applicationData }))
          .catch((applicationError) => ({ applicationError })),
        searchEligibilityCheck(selectedPhone)
          .then((eligibilityData) => ({ eligibilityData }))
          .catch((eligibilityError) => ({ eligibilityError })),
      ]);

      if (
        applicationLookup.applicationError &&
        eligibilityLookup.eligibilityError
      ) {
        throw eligibilityLookup.eligibilityError;
      }

      const response = {
        ...(eligibilityLookup.eligibilityData || {
          phone: selectedPhone,
          searched_phone: selectedPhone,
        }),
        applicationLookup: applicationLookup.applicationData || null,
      };
      const record = mapEligibilitySearchResponse(response, {
        ...fallbackRecord,
        phone: fallbackRecord.phone || selectedPhone,
      });
      let nextRecord = record;

      try {
        const dedupeData = await fetchLeadApplicationDedupe({
          phone: normalizeDedupeLookupValue(record.phone || selectedPhone),
          pan: normalizeDedupeLookupValue(record.panNumber).toUpperCase(),
        });

        nextRecord = {
          ...nextRecord,
          dedupe: dedupeData,
        };
      } catch {
        nextRecord = {
          ...nextRecord,
          dedupe: null,
        };
      }

      if (!eligibilityLookup.eligibilityError) {
        try {
          const bureauData = await pullCrifBureauReportCallCenter(
            buildEligibilityBureauPayload(record, response, fallbackRecord),
          );

          nextRecord = {
            ...nextRecord,
            bureau: mapEligibilityBureauData(bureauData),
          };
        } catch {
          // Keep any dedupe details already attached when bureau pull fails.
        }
      }

      setSelectedRecord(nextRecord);
      setPhoneNumber(nextRecord.phone || selectedPhone);
      setEligibilitySearchError(
        eligibilityLookup.eligibilityError
          ? `Eligibility info unavailable: ${eligibilityLookup.eligibilityError.message}`
          : "",
      );
    } catch (error) {
      setSelectedRecord(null);
      setEligibilitySearchError(error.message);
    } finally {
      setEligibilitySearchPhone("");
    }
  };

  const handleViewRecord = (record) => {
    searchEligibilityDetails(record.phone, record);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    searchEligibilityDetails(phoneNumber);
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setEligibilityListPage(1);
  };

  const handleOpenPilInfo = async (record) => {
    if (!record) {
      return;
    }

    setIsEligibilityResultOpen(false);
    setIsPilInfoOpen(false);
    setPilInfoDetails(null);
    setPilInfoError("");
    setIsPilInfoLoading(true);

    try {
      const response = await searchPilInfo({
        searchType: "phone",
        phone: record.phone,
      });

      setPilInfoDetails(mapPilInfoResponse(response));
      setIsPilInfoOpen(true);
    } catch (error) {
      setPilInfoError(error.message);
    } finally {
      setIsPilInfoLoading(false);
    }
  };

  const handleOpenEligibilityResult = async (record) => {
    if (!record) {
      return;
    }

    setIsEligibilityResultOpen(false);
    setIsPilInfoOpen(false);
    setEligibilityResultForm(null);
    setEligibilityResultOptions(null);
    setEligibilityResultError("");
    setIsEligibilityResultLoading(true);

    try {
      const [formResponse, optionsResponse] = await Promise.all([
        fetchEligibilityResultForm({
          contactId: record.contactId,
          phone: record.phone,
        }),
        fetchEligibilityCheckOptions(),
      ]);

      setEligibilityResultForm(formResponse);
      setEligibilityResultOptions(optionsResponse);
      setIsEligibilityResultOpen(true);
    } catch (error) {
      setEligibilityResultError(error.message);
    } finally {
      setIsEligibilityResultLoading(false);
    }
  };

  const handleClosePilInfo = () => {
    setIsPilInfoOpen(false);
    setIsPilInfoLoading(false);
  };

  const handleCloseEligibilityResult = () => {
    setIsEligibilityResultOpen(false);
    setIsEligibilityResultLoading(false);
  };

  const handleSaveEligibilityResult = async (payload) => {
    const response = await saveEligibilityCheckResult(payload);
    setEligibilityListReloadKey((currentKey) => currentKey + 1);
    return response;
  };

  const listHeading =
    eligibilityListTitle || `Eligibility Check ${activeTabLabel}`;
  const listTotal = eligibilityListTotal || activeRecords.length;
  const totalPages = eligibilityPagination.totalPages || 1;
  const showPagination = eligibilityPagination.enabled && totalPages > 1;
  const canGoToPreviousPage = showPagination && eligibilityListPage > 1;
  const canGoToNextPage = showPagination && eligibilityListPage < totalPages;

  return (
    <ActionPageLayout
      title="Eligibility Check"
      icon={
        <FaTh className="h-6 w-6 flex-none text-[#6d4b8a]" aria-hidden="true" />
      }
    >
      <section className="mt-6">
        <form
          className="grid max-w-[56rem] grid-cols-[minmax(16rem,37rem)_minmax(12rem,18.25rem)] items-end gap-2 max-[720px]:max-w-none max-[720px]:grid-cols-1"
          aria-label="Eligibility check search"
          onSubmit={handleSearch}
        >
          <label className="block" htmlFor="eligibility-phone">
            <span className="mb-1.5 block text-base">Phone Number</span>
            <input
              className={inputClass}
              id="eligibility-phone"
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder="Enter phone number"
              value={phoneNumber}
              onChange={(event) =>
                setPhoneNumber(
                  event.target.value.replace(/\D/g, "").slice(0, 10),
                )
              }
            />
          </label>
          <button
            className="inline-flex h-[38px] min-w-[10rem] items-center justify-center gap-2 rounded-md bg-[#1773f8] px-6 text-base font-medium text-white shadow transition hover:bg-[#0d63dc] focus-visible:bg-[#0d63dc] disabled:cursor-not-allowed disabled:opacity-70"
            type="submit"
            aria-busy={Boolean(eligibilitySearchPhone)}
            disabled={Boolean(eligibilitySearchPhone)}
          >
            <FaSearch className="h-4 w-4 flex-none" aria-hidden="true" />
            {eligibilitySearchPhone ? "Searching..." : "Search"}
          </button>
        </form>

        {eligibilitySearchError ? (
          <div
            className="mt-3 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-sm text-[#842029]"
            role="alert"
          >
            {eligibilitySearchError}
          </div>
        ) : null}
      </section>

      {selectedRecord && (
        <EligibilityDetailView
          eligibilityResultError={eligibilityResultError}
          isEligibilityResultLoading={isEligibilityResultLoading}
          isPilInfoLoading={isPilInfoLoading}
          pilInfoError={pilInfoError}
          record={selectedRecord}
          onOpenEligibilityResult={() =>
            handleOpenEligibilityResult(selectedRecord)
          }
          onOpenPilInfo={() => handleOpenPilInfo(selectedRecord)}
        />
      )}

      {selectedRecord && isEligibilityResultOpen && (
        <EligibilityResultModal
          record={selectedRecord}
          resultForm={eligibilityResultForm}
          resultOptions={eligibilityResultOptions}
          onSave={handleSaveEligibilityResult}
          onClose={handleCloseEligibilityResult}
        />
      )}

      {selectedRecord && isPilInfoOpen && (
        <PilInfoModal
          pilInfoDetails={pilInfoDetails}
          record={selectedRecord}
          onClose={handleClosePilInfo}
        />
      )}

      <section className="mt-7" aria-label="Eligibility checks">
        <h2 className="mb-3 flex items-center gap-3 text-[1.75rem] font-bold text-[#171717] max-[720px]:text-2xl">
          <FaClipboardList
            className="h-8 w-8 flex-none text-[#c98655]"
            aria-hidden="true"
          />
          Eligibility Checks
        </h2>

        <div className="border-b border-[#d9dfe8]">
          <div className="flex items-end gap-3 max-[720px]:gap-1">
            {tabItems.map((tab) => (
              <button
                className={`inline-flex h-10 min-w-[8.5rem] items-center justify-center gap-2 px-4 text-base transition max-[720px]:min-w-0 max-[720px]:flex-1 max-[720px]:px-3 ${
                  activeTab === tab.key
                    ? "rounded-t-md border border-b-white border-[#d9dfe8] bg-white font-medium text-[#071426]"
                    : "border border-transparent bg-white text-[#0066ff] hover:bg-[#f8fafc]"
                }`}
                key={tab.label}
                type="button"
                aria-pressed={activeTab === tab.key}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <h3 className="mb-3 mt-5 text-base font-semibold text-[#0066ff]">
          {listHeading} ({listTotal})
        </h3>

        {isEligibilityListLoading ? (
          <div
            className="rounded-md border border-[#b6d4fe] bg-[#e7f1ff] px-4 py-4 text-base leading-[1.45] text-[#084298]"
            role="status"
          >
            Loading eligibility checks...
          </div>
        ) : eligibilityListError ? (
          <div
            className="rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-4 text-base leading-[1.45] text-[#842029]"
            role="alert"
          >
            {eligibilityListError}
          </div>
        ) : activeRecords.length ? (
          <>
            <RecordsTable
              records={activeRecords}
              searchingPhone={eligibilitySearchPhone}
              onViewRecord={handleViewRecord}
            />

            {showPagination ? (
              <div className="mt-4 flex flex-wrap items-center justify-start gap-3">
                <span className="text-sm font-medium text-[#4b5563]">
                  Page {eligibilityListPage} of {totalPages}
                </span>

                {canGoToPreviousPage ? (
                  <button
                    className="inline-flex h-[36px] items-center justify-center rounded-md border border-[#d1d5db] bg-white px-4 text-sm font-medium text-[#374151] transition hover:bg-[#f9fafb] focus-visible:bg-[#f9fafb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9fc8ff]"
                    type="button"
                    onClick={() =>
                      setEligibilityListPage((currentPage) =>
                        Math.max(1, currentPage - 1),
                      )
                    }
                  >
                    Previous
                  </button>
                ) : null}

                {canGoToNextPage ? (
                  <button
                    className="inline-flex h-[36px] items-center justify-center rounded-md bg-[#1773f8] px-4 text-sm font-medium text-white transition hover:bg-[#0d63dc] focus-visible:bg-[#0d63dc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9fc8ff]"
                    type="button"
                    onClick={() =>
                      setEligibilityListPage((currentPage) =>
                        Math.min(totalPages, currentPage + 1),
                      )
                    }
                  >
                    Next
                  </button>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <div
            className="rounded-md border border-[#ffd966] bg-[#fff3cd] px-4 py-4 text-base leading-[1.45] text-[#5b3b00]"
            role="status"
          >
            No data found under this filter.
          </div>
        )}
      </section>
    </ActionPageLayout>
  );
}

export default EligibilityCheckPage;
